// Custom Exam Study Plan — pure logic and localStorage persistence.
//
// Plans are generated fresh each day on first load. Within the same calendar
// day, the plan is loaded from cache so it stays stable even if the user
// closes and reopens the app.

import type { WikiExamSyllabus } from '@/lib/wikiParser'
import type { ConceptMasteryRecord, MasteryState } from '@/lib/mastery'
import { DECAY_DAYS_LEVEL1, DECAY_DAYS_LEVEL2, DECAY_DAYS_LEVEL3, decayIfStale } from '@/lib/mastery'
import { buildMasteryLookup, lookupConceptRecord, resolveConceptState } from '@/lib/conceptMatch'
import { orderConceptsForPlan } from '@/lib/studyPlanOrder'
import { localDayKey } from '@/lib/streak'

// Bump when the generation logic changes in a way that should invalidate
// already-cached plans (local + server), forcing one clean regeneration even
// within the same calendar day. v2: fixed plans generated from empty mastery
// records / aliased-concept lookups that left mastered concepts looking 'new'.
// v3: concept names now use the canonical page name instead of the syllabus
// display alias (e.g. "Callable Bond" not "Callable").
// v4: even daily-load distribution; proactive scheduling for level3 concepts
// approaching the decay threshold.
// v5: a concept linked from more than one syllabus topic is scheduled once
// instead of once per topic (which produced out-of-order level targets).
// v6: concepts are introduced in teaching order instead of alphabetically
// (see lib/studyPlanOrder.ts).
// v7: 'strong_key' orders by topic weight alone, heaviest first.
// v8: new concepts are paced evenly over the runway; no session is booked past
// the last study day or after its concept would decay; last_correct_at is read
// as a local day.
export const PLAN_CACHE_VERSION = 8

// ── Types ─────────────────────────────────────────────────────────────────────

export type TargetStrengthLevel = 'strong_all' | 'strong_key'

export type QuickSetPreset = '1d' | '1w' | '2w' | '1m' | '2m' | '3m' | '6m' | '8m'

export const QUICK_SET_LABELS: Record<QuickSetPreset, string> = {
  '1d': '1 day before',
  '1w': '1 week before',
  '2w': '2 weeks before',
  '1m': '1 month before',
  '2m': '2 months before',
  '3m': '3 months before',
  '6m': '6 months before',
  '8m': '8 months before',
}

export const QUICK_SET_PRESETS: QuickSetPreset[] = ['1d', '1w', '2w', '1m', '2m', '3m', '6m', '8m']

export interface StudyPlanConfig {
  targetReadyDate: string | null   // YYYY-MM-DD; null = unconfigured
  targetStrengthLevel: TargetStrengthLevel
  planStartDate: string | null     // YYYY-MM-DD; set on first save, used for day numbering
  examVariant?: string | null      // regional variant ID for localized exams (e.g. 'US', 'CA', 'INT')
}

export interface ConceptAssignment {
  conceptName: string
  topicName: string
  topicWeight?: string
  scheduledDate: string  // YYYY-MM-DD
  initialState: MasteryState  // concept's state at scheduling time; used to derive today's target
}

export type PacingStatus = 'on_track' | 'behind' | 'ahead' | 'review_mode' | 'target_passed'

export interface StudyPlan {
  examId: string
  planVersion?: number           // PLAN_CACHE_VERSION at generation; older/absent => invalid
  generatedDate: string          // YYYY-MM-DD — used to detect stale cache
  config: StudyPlanConfig
  /** The exam date the plan was built against. A cached plan is only reused for
   *  the same date; absent on plans cached before it was recorded. */
  examDate?: string | null
  todaysConcepts: string[]       // concept names assigned today
  assignments: ConceptAssignment[]  // all upcoming assignments (today + future)
  dayNumber: number              // 1-indexed position in the overall plan
  totalDays: number              // total planned days from planStartDate → targetReadyDate
  daysRemaining: number          // days left from today → effectiveReadyDate
  conceptsPerDay: number         // average new concepts to introduce per day to stay on pace
  status: PacingStatus
  reviewConcepts: string[]       // weakest mastered concepts for spaced review
  effectiveReadyDate: string     // may differ from config if target has passed
  targetPassedFallback: boolean  // true when we fell back to exam date
}

// ── Date helpers ──────────────────────────────────────────────────────────────
//
// Every day key in the app is the user's **local** calendar day. That matters
// well beyond cosmetics: `daily_completions.day`, the device-local level-up keys
// (lib/dailyProgressStore), an assignment's `scheduledDate` and the Study
// Schedule heatmap's cells are all compared as strings, and the heatmap draws
// its grid from local dates. Keying any of them off `toISOString()` — UTC — moves
// the day boundary to 5–7pm for a North American user: the evening's work is
// filed under tomorrow, the Today card rolls over early, and the heatmap can
// never paint that work onto a cell (tomorrow is a *future* square). So format
// through `localDayKey`, never `toISOString().slice(0, 10)`.

/** Today as `YYYY-MM-DD` in the user's local timezone. */
export function todayISO(): string {
  return localDayKey(new Date())
}

export function addDays(dateISO: string, days: number): string {
  const d = new Date(dateISO + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return localDayKey(d)
}

export function daysBetween(from: string, to: string): number {
  const a = new Date(from + 'T12:00:00')
  const b = new Date(to + 'T12:00:00')
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24))
}

export function applyPreset(examDate: string, preset: QuickSetPreset): string {
  const d = new Date(examDate + 'T12:00:00')
  switch (preset) {
    case '1d':  d.setDate(d.getDate() - 1);    break
    case '1w':  d.setDate(d.getDate() - 7);    break
    case '2w':  d.setDate(d.getDate() - 14);   break
    case '1m':  d.setMonth(d.getMonth() - 1);  break
    case '2m':  d.setMonth(d.getMonth() - 2);  break
    case '3m':  d.setMonth(d.getMonth() - 3);  break
    case '6m':  d.setMonth(d.getMonth() - 6);  break
    case '8m':  d.setMonth(d.getMonth() - 8);  break
  }
  return localDayKey(d)
}

export function formatReadableDate(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

// ── localStorage helpers ──────────────────────────────────────────────────────

const CONFIG_KEY = 'actuarial_study_plan_config_v1_'
const PLAN_KEY   = 'actuarial_study_plan_v1_'

export function loadStudyPlanConfig(examId: string): StudyPlanConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY + examId)
    if (raw) return JSON.parse(raw) as StudyPlanConfig
  } catch { /* ignore */ }
  return { targetReadyDate: null, targetStrengthLevel: 'strong_all', planStartDate: null }
}

export function saveStudyPlanConfig(examId: string, config: StudyPlanConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY + examId, JSON.stringify(config))
  } catch { /* quota */ }
}

export function loadCachedStudyPlan(examId: string): StudyPlan | null {
  try {
    const raw = localStorage.getItem(PLAN_KEY + examId)
    if (!raw) return null
    const plan = JSON.parse(raw) as StudyPlan
    if (plan.planVersion !== PLAN_CACHE_VERSION) return null  // generated by outdated logic
    if (plan.generatedDate === todayISO()) return plan
    return null  // stale
  } catch {
    return null
  }
}

export function saveCachedStudyPlan(plan: StudyPlan): void {
  try {
    localStorage.setItem(PLAN_KEY + plan.examId, JSON.stringify(plan))
  } catch { /* quota */ }
}

// ── Plan generation ───────────────────────────────────────────────────────────

function parseTopicWeight(weight: string): number {
  const rangeMatch = weight.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)%/)
  if (rangeMatch) return (parseFloat(rangeMatch[1]) + parseFloat(rangeMatch[2])) / 2
  const singleMatch = weight.match(/(\d+(?:\.\d+)?)%/)
  if (singleMatch) return parseFloat(singleMatch[1])
  return 1
}

interface ConceptEntry {
  name: string
  target?: string | null   // raw [[target]] base name; mastery may be stored under this
  topicName: string
  topicWeight?: string
  numericWeight: number
}

/** Identity of the concept page an entry points at — two syllabus entries sharing
 *  it are the same concept, however they're displayed or linked. */
function conceptKey(c: ConceptEntry): string {
  const base = c.target?.split('/').pop()?.replace(/\.md$/i, '').trim()
  return (base || c.name).toLowerCase()
}

interface GenerateInput {
  examId: string
  syllabus: WikiExamSyllabus
  masteryRecords: ConceptMasteryRecord[]
  config: StudyPlanConfig
  examDate: string | null
  /** Concept slugs levelled up today — used to keep today's plan grounded in actual quiz progress
   *  even when the plan regenerates mid-day (e.g. after a config change). */
  todaysLevelUps?: string[]
}

export function generateStudyPlan(input: GenerateInput): StudyPlan {
  const { examId, syllabus, masteryRecords, config, examDate, todaysLevelUps } = input
  const today = todayISO()
  const now = new Date()

  // Build mastery lookup (normalised to lowercase). Stored slugs use the
  // concept's file/base name, so resolveConceptState tries display name then
  // target — keeping aliased concepts (e.g. [[Bond Price|Price]]) in sync.
  const masteryBySlug = buildMasteryLookup(masteryRecords.filter(r => r.exam_id === examId))

  // Collect all concepts with topic metadata.
  //
  // The syllabus parser deduplicates concepts *within* a topic, not across them,
  // so a concept linked from two objectives (e.g. Exam 7 links [[Reinsurance]]
  // from both the data-prep preamble and the Reinsurance objective) arrives here
  // twice. Scheduling each entry separately would give the same concept two
  // independent three-stage pipelines, which interleave on the calendar: the plan
  // would say "Reinsurance → Level 2" on one day, "→ Level 1" the next and
  // "→ Level 3" the day after. The duplicate sessions also inflate the daily load
  // and the mastered/total ratio behind the pacing status.
  //
  // So keep one entry per concept — keyed by the concept page it points at, since
  // that's what mastery is stored under — and let the heaviest topic own it so
  // 'strong_key' priority reflects the concept's biggest stake in the exam.
  const allConcepts: ConceptEntry[] = []
  const conceptIndexByKey = new Map<string, number>()
  for (const topic of syllabus.topics) {
    const numericWeight = topic.weight ? parseTopicWeight(topic.weight) : 1
    for (const c of topic.concepts) {
      const entry: ConceptEntry = {
        name: c.name,
        target: c.target,
        topicName: topic.name,
        topicWeight: topic.weight,
        numericWeight,
      }
      const key = conceptKey(entry)
      const existingIdx = conceptIndexByKey.get(key)
      if (existingIdx === undefined) {
        conceptIndexByKey.set(key, allConcepts.length)
        allConcepts.push(entry)
      } else if (numericWeight > allConcepts[existingIdx].numericWeight) {
        allConcepts[existingIdx] = entry
      }
    }
  }

  const getState = (c: ConceptEntry) => resolveConceptState(masteryBySlug, c, now)

  const mastered   = allConcepts.filter(c => getState(c) === 'level3')
  const unmastered = allConcepts.filter(c => getState(c) !== 'level3')

  // ── Review mode: every concept already level3 ────────────────────────────
  if (unmastered.length === 0) {
    const effective = examDate ?? addDays(today, 30)
    const rmDaysRemaining = Math.max(1, daysBetween(today, effective))

    // Identify level3 concepts that will decay strictly before the target date,
    // sorted by urgency so the soonest-to-decay surface first.
    const rmMaintenance = mastered
      .flatMap(c => {
        const rec = lookupConceptRecord(masteryBySlug, c)
        if (!rec?.last_correct_at) return []
        const daysSinceCorrect = daysBetween(localDayKey(new Date(rec.last_correct_at)), today)
        const daysUntilDecay = DECAY_DAYS_LEVEL3 - daysSinceCorrect
        if (daysUntilDecay <= 0 || daysUntilDecay >= rmDaysRemaining) return []
        return [{ c, daysUntilDecay }]
      })
      .sort((a, b) => a.daysUntilDecay - b.daysUntilDecay)

    const maintenanceAssignments: ConceptAssignment[] = rmMaintenance.map(({ c, daysUntilDecay }) => ({
      conceptName: c.name,
      topicName: c.topicName,
      topicWeight: c.topicWeight,
      // Open the review window 7 days before decay; show it immediately if urgent.
      scheduledDate: addDays(today, Math.max(0, daysUntilDecay - 7)),
      initialState: 'level3' as MasteryState,
    }))

    const reviewConcepts = [...mastered]
      .sort((a, b) => {
        const ra = lookupConceptRecord(masteryBySlug, a)
        const rb = lookupConceptRecord(masteryBySlug, b)
        const ta = ra?.last_correct_at ? new Date(ra.last_correct_at).getTime() : 0
        const tb = rb?.last_correct_at ? new Date(rb.last_correct_at).getTime() : 0
        return ta - tb  // oldest first
      })
      .slice(0, Math.min(5, mastered.length))
      .map(c => c.name)

    // Today: urgent maintenance concepts first, then stalest-review suggestions.
    const urgentToday = rmMaintenance
      .filter(({ daysUntilDecay }) => daysUntilDecay <= 7)
      .map(({ c }) => c.name)
    const todaysConcepts = [...new Set([...urgentToday, ...reviewConcepts])]

    return {
      examId,
      planVersion: PLAN_CACHE_VERSION,
      generatedDate: today,
      config,
      examDate,
      todaysConcepts,
      assignments: maintenanceAssignments,
      dayNumber: 1,
      totalDays: 1,
      daysRemaining: Math.max(0, daysBetween(today, effective)),
      conceptsPerDay: 0,
      status: 'review_mode',
      reviewConcepts,
      effectiveReadyDate: effective,
      targetPassedFallback: false,
    }
  }

  // ── Resolve effective target date ─────────────────────────────────────────
  let effectiveReadyDate = config.targetReadyDate
  let targetPassedFallback = false

  const targetHasPassed = !effectiveReadyDate || daysBetween(today, effectiveReadyDate) <= 0

  if (targetHasPassed) {
    if (examDate && daysBetween(today, examDate) > 0) {
      effectiveReadyDate = examDate
      if (config.targetReadyDate && daysBetween(today, config.targetReadyDate) <= 0) {
        targetPassedFallback = true
      }
    } else {
      effectiveReadyDate = addDays(today, 30)
    }
  }

  const daysRemaining = Math.max(1, daysBetween(today, effectiveReadyDate!))

  // ── Sort unmastered concepts by priority ──────────────────────────────────
  // forgotten > level1 > level2 > new (partially-learned beats brand-new)
  const stateOrder: Record<string, number> = { forgotten: 0, level1: 1, level2: 2, new: 3 }

  // Within a state, concepts are introduced in teaching order — syllabus order,
  // or heaviest topic first when the strategy asks for it (lib/studyPlanOrder.ts).
  // Never alphabetical: on a fresh account every concept is New, so the tiebreak
  // *is* the plan, and "A" is not a reason to learn something first.
  const teachingRank = new Map<ConceptEntry, number>()
  orderConceptsForPlan(allConcepts, {
    strategy: config.targetStrengthLevel,
  }).forEach((c, i) => teachingRank.set(c, i))

  const sortedUnmastered = [...unmastered].sort((a, b) => {
    const stateDiff = (stateOrder[getState(a)] ?? 3) - (stateOrder[getState(b)] ?? 3)
    if (stateDiff !== 0) return stateDiff
    return (teachingRank.get(a) ?? 0) - (teachingRank.get(b) ?? 0)
  })

  // ── Spacing-aware scheduling ──────────────────────────────────────────────
  // Rules:
  //   - new/forgotten: introduced at an even pace across the runway, at most
  //     MAX_NEW_PER_DAY on any day
  //   - level1 → level2: earliest after 1 day gap from last_correct_at
  //   - level2 → level3: earliest after 2 day gap from last_correct_at
  //   - max 1 level advance per concept per day (enforced by mastery.ts)
  //
  // Every session also has a *deadline*: the last study day (the day before the
  // ready date — that date is when the learner means to be ready, not one more
  // day of work), less the days its later stages still need, and never later
  // than the day the concept would decay out of the level the session builds on.
  // A review booked after that decay would target a level the concept no longer
  // has a footing for — "→ Level 3" on a concept back at Level 1.
  //
  // Scheduling works in day offsets from today; `dayAt` turns one into a date.

  const MAX_NEW_PER_DAY = 5

  const lastStudyDay = daysRemaining - 1
  // Reserve 3 days at the end for the last introduced concept to complete its
  // full path (intro→level1 on D, level1→level2 on D+1, level2→level3 on D+3).
  const introDays = Math.max(1, daysRemaining - 3)

  const newAndForgottenCount = sortedUnmastered.filter(
    c => { const s = getState(c); return s === 'new' || s === 'forgotten' },
  ).length
  const dailyNewLimit = Math.min(
    MAX_NEW_PER_DAY,
    Math.max(1, Math.ceil(newAndForgottenCount / introDays)),
  )

  const dateCache: string[] = []
  const dayAt = (offset: number) => (dateCache[offset] ??= addDays(today, offset))

  /** Local day offset of a stored timestamp (negative for the past). */
  const offsetOfTimestamp = (iso: string) => daysBetween(today, localDayKey(new Date(iso)))

  /** The last day offset (≥ 0) on which a concept still holds the level it is
   *  at now — decay can land mid-day, so a day only counts if the level lasts
   *  through to its end. */
  function lastDayAtCurrentLevel(rec: ConceptMasteryRecord): number {
    const state = decayIfStale(rec, now).state
    let last = 0
    for (let d = 0; d <= DECAY_DAYS_LEVEL3 + 1; d++) {
      if (decayIfStale(rec, new Date(dayAt(d) + 'T23:59:59')).state !== state) break
      last = d
    }
    return last
  }

  function getEligibleDay(c: ConceptEntry): number {
    const state = getState(c)
    const rec = lookupConceptRecord(masteryBySlug, c)
    if (!rec?.last_correct_at) return 0
    if (state === 'level1') return Math.max(0, offsetOfTimestamp(rec.last_correct_at) + 1)
    if (state === 'level2') return Math.max(0, offsetOfTimestamp(rec.last_correct_at) + 2)
    return 0  // new / forgotten
  }

  // Level3 concepts that will cross the decay threshold before the target date must
  // be scheduled for a refresher before they slip. Sort by urgency so the soonest-
  // to-decay concepts claim the nearest available slots first.
  const maintenanceCandidates = mastered
    .flatMap(c => {
      const rec = lookupConceptRecord(masteryBySlug, c)
      if (!rec?.last_correct_at) return []
      const daysUntilDecay = DECAY_DAYS_LEVEL3 + offsetOfTimestamp(rec.last_correct_at)
      // Only schedule if the concept decays strictly before the target date.
      // A concept that decays on or after the target day doesn't need early intervention.
      if (daysUntilDecay <= 0 || daysUntilDecay >= daysRemaining) return []
      return [{ c, daysUntilDecay }]
    })
    .sort((a, b) => a.daysUntilDecay - b.daysUntilDecay)

  // Compute how many total sessions (intro + each review stage + maintenance) remain,
  // then aim each day at that average to prevent pile-ups when many concepts converge
  // to the same eligible date.
  const totalSessions = sortedUnmastered.reduce((sum, c) => {
    const s = getState(c)
    if (s === 'new' || s === 'forgotten') return sum + 3
    if (s === 'level1') return sum + 2
    if (s === 'level2') return sum + 1
    return sum
  }, maintenanceCandidates.length)
  const targetDailyLoad = Math.ceil(totalSessions / Math.max(1, daysRemaining))
  const sessionsByDay: number[] = []   // offset → sessions booked that day
  const introsByDay: number[] = []     // offset → new introductions booked that day
  const loadOn = (d: number) => sessionsByDay[d] ?? 0

  // Book a review on the earliest day in [earliest, latest] still under the daily
  // load target. The load target is the softer of the two limits: when every day
  // in the window is full, the review goes on the window's least-loaded day
  // rather than past its deadline. Only an empty window — the deadline can't be
  // met at all — sends it past, onto the first day with room.
  function book(earliest: number, latest: number): number {
    let fallback = -1
    for (let d = earliest; d <= latest; d++) {
      if (loadOn(d) < targetDailyLoad) return take(d)
      if (fallback === -1 || loadOn(d) < loadOn(fallback)) fallback = d
    }
    if (fallback !== -1) return take(fallback)
    let d = Math.max(earliest, latest + 1)
    while (loadOn(d) >= targetDailyLoad) d++
    return take(d)
  }
  function take(d: number): number {
    sessionsByDay[d] = loadOn(d) + 1
    return d
  }

  const assignments: ConceptAssignment[] = []
  let lastBookedDay = 0
  function assign(c: ConceptEntry, day: number, initialState: MasteryState) {
    lastBookedDay = Math.max(lastBookedDay, day)
    assignments.push({
      conceptName: c.name,
      topicName: c.topicName,
      topicWeight: c.topicWeight,
      scheduledDate: dayAt(day),
      initialState,
    })
  }

  // The two review stages of the pipeline, each booked no earlier than `earliest`
  // and no later than `holdsUntil` — the last day the level it builds on lasts —
  // or than the stages after it allow. A level earned on a booked day holds for
  // its decay window from then: through day + DECAY_DAYS - 1 at least.
  function bookLevel2(c: ConceptEntry, earliest: number, holdsUntil: number) {
    const day = book(earliest, Math.min(lastStudyDay - 2, holdsUntil))
    assign(c, day, 'level1')
    bookLevel3(c, day + 2, day + DECAY_DAYS_LEVEL2 - 1)
  }
  function bookLevel3(c: ConceptEntry, earliest: number, holdsUntil: number) {
    assign(c, book(earliest, Math.min(lastStudyDay, holdsUntil)), 'level2')
  }

  // Zero: schedule pre-emptive refreshers for level3 concepts that will decay
  // before the target date, urgency-first so the soonest-to-decay get the
  // nearest open slots. Reviews aren't shown until they enter their 7-day window,
  // so concepts with distant deadlines don't crowd today's plan unnecessarily.
  for (const { c, daysUntilDecay } of maintenanceCandidates) {
    assign(c, book(Math.max(0, daysUntilDecay - 7), daysUntilDecay - 1), 'level3')
  }

  // First: schedule level1/level2 on their earliest available slot (respecting the
  // minimum gap and the day the concept would decay), then pre-schedule the next
  // pipeline stage.
  for (const c of sortedUnmastered) {
    const state = getState(c)
    if (state !== 'level1' && state !== 'level2') continue
    const rec = lookupConceptRecord(masteryBySlug, c)!
    if (state === 'level1') bookLevel2(c, getEligibleDay(c), lastDayAtCurrentLevel(rec))
    else bookLevel3(c, getEligibleDay(c), lastDayAtCurrentLevel(rec))
  }

  // Second: introduce new/forgotten concepts at an even pace across the intro
  // days — concept i is due no earlier than day ⌊i · introDays / N⌋ — so a short
  // list on a long runway is spread out rather than finished weeks early, then
  // pre-schedule all three pipeline stages so the true workload is visible from
  // day one.
  let introIndex = 0
  for (const c of sortedUnmastered) {
    const state = getState(c)
    if (state !== 'new' && state !== 'forgotten') continue
    // The pace itself sets the day, not the load target: the target is the
    // average of what the pace produces, so holding intros to it would push each
    // one into the next one's day and the whole run past the deadline.
    let introDay = Math.floor((introIndex++ * introDays) / newAndForgottenCount)
    while ((introsByDay[introDay] ?? 0) >= dailyNewLimit) introDay++
    introsByDay[introDay] = (introsByDay[introDay] ?? 0) + 1
    take(introDay)
    // Stage 1: introduce (new/forgotten → level1)
    assign(c, introDay, state)
    // Stages 2 and 3: level1 → level2 (1-day gap), level2 → level3 (2-day gap)
    bookLevel2(c, introDay + 1, introDay + DECAY_DAYS_LEVEL1 - 1)
  }

  // Deduplicate todaysConcepts — a concept should appear at most once today
  let todaysConcepts = [...new Set(
    assignments
      .filter(a => a.scheduledDate === today)
      .map(a => a.conceptName)
  )]

  // If concepts were already levelled up today, prefer them over fresh unlearned ones.
  // This keeps today's plan grounded in actual quiz progress when the plan regenerates
  // mid-day (e.g. after a config change): the user sees what they actually worked on,
  // not a list of brand-new concepts they haven't touched yet.
  //
  // A level-up is keyed by the slug mastery is stored under — the concept page's
  // name, which an aliased syllabus link ([[Bond Price|Price]]) doesn't display —
  // so each is resolved to this plan's concept by either name. One that isn't on
  // this syllabus (another exam's) is dropped, and a concept advanced twice today
  // is listed once.
  const conceptByAnyName = new Map<string, ConceptEntry>()
  for (const c of allConcepts) {
    conceptByAnyName.set(conceptKey(c), c)
    conceptByAnyName.set(c.name.toLowerCase(), c)
  }
  const levelledUpToday = [...new Set(
    (todaysLevelUps ?? []).flatMap(slug => {
      const c = conceptByAnyName.get(slug.toLowerCase())
      return c ? [c.name] : []
    }),
  )]
  if (levelledUpToday.length > 0) {
    const leveledUpSet = new Set(levelledUpToday.map(s => s.toLowerCase()))
    const originalCount = todaysConcepts.length
    const fresh = todaysConcepts.filter(n => !leveledUpSet.has(n.toLowerCase()))
    todaysConcepts = [...levelledUpToday, ...fresh].slice(0, originalCount)
  }

  // conceptsPerDay = average daily new-concept introductions needed to stay on pace.
  // Using neededNewPerDay (uncapped) so the displayed number reflects the true required
  // rate, not the peak pipeline count which inflates due to staged assignments converging.
  const neededNewPerDay = Math.ceil(newAndForgottenCount / introDays)
  const conceptsPerDay = Math.max(1, neededNewPerDay)

  // ── Pacing status ─────────────────────────────────────────────────────────
  // A schedule is "behind" when unmastered concepts can't fit in the remaining
  // days: introducing them would take more than MAX_NEW_PER_DAY a day, or some
  // session could only be booked after the last study day.
  let status: PacingStatus = 'on_track'
  if (targetPassedFallback) {
    status = 'target_passed'
  } else if (neededNewPerDay > MAX_NEW_PER_DAY || lastBookedDay > lastStudyDay) {
    status = 'behind'
  } else {
    const planStartDate = config.planStartDate ?? today
    const totalDaysFromStart = Math.max(1, daysBetween(planStartDate, effectiveReadyDate!))
    const daysElapsed = totalDaysFromStart - daysRemaining
    const expectedMasteredFraction = daysElapsed / totalDaysFromStart
    const actualMasteredFraction = mastered.length / allConcepts.length
    if (daysElapsed > 2 && actualMasteredFraction > expectedMasteredFraction + 0.15) {
      status = 'ahead'
    }
  }

  // ── Day numbering ─────────────────────────────────────────────────────────
  const planStartDate = config.planStartDate ?? today
  const totalDays = Math.max(1, daysBetween(planStartDate, effectiveReadyDate!))
  const dayNumber = Math.max(1, totalDays - daysRemaining + 1)

  // ── Spaced review suggestions (for concepts marked strong that are getting stale)
  const reviewConcepts = [...mastered]
    .sort((a, b) => {
      const ra = lookupConceptRecord(masteryBySlug, a)
      const rb = lookupConceptRecord(masteryBySlug, b)
      const ta = ra?.last_correct_at ? new Date(ra.last_correct_at).getTime() : 0
      const tb = rb?.last_correct_at ? new Date(rb.last_correct_at).getTime() : 0
      return ta - tb
    })
    .slice(0, 3)
    .map(c => c.name)

  return {
    examId,
    planVersion: PLAN_CACHE_VERSION,
    generatedDate: today,
    config,
    examDate,
    todaysConcepts,
    assignments,
    dayNumber,
    totalDays,
    daysRemaining,
    conceptsPerDay,
    status,
    reviewConcepts,
    effectiveReadyDate: effectiveReadyDate!,
    targetPassedFallback,
  }
}

// ── Helpers for consumers ─────────────────────────────────────────────────────

export function getScheduledDate(
  conceptName: string,
  plan: StudyPlan | null,
): string | null {
  if (!plan) return null
  const today = todayISO()
  const dates = plan.assignments
    .filter(x => x.conceptName.toLowerCase() === conceptName.toLowerCase())
    .map(x => x.scheduledDate)
    .sort()
  // Return the earliest upcoming date (today or future); fall back to the last known date
  return dates.find(d => d >= today) ?? dates[dates.length - 1] ?? null
}

export function isScheduledToday(conceptName: string, plan: StudyPlan | null): boolean {
  const today = todayISO()
  const d = getScheduledDate(conceptName, plan)
  return d === today
}

// How many days until a concept is scheduled (negative if overdue/today)
export function daysUntilScheduled(conceptName: string, plan: StudyPlan | null): number | null {
  const d = getScheduledDate(conceptName, plan)
  if (!d) return null
  return daysBetween(todayISO(), d)
}

// ── Today's plan question selection ───────────────────────────────────────────

interface CoverageQuestion {
  id: string
  wiki_link: string[]
}

// Resolve a question's wiki_link entries to the lowercased concept names they
// reference, matching the normalization used to build today's concept set.
function linkedConceptNames(wikiLinks: string[]): Set<string> {
  const names = new Set<string>()
  for (const link of wikiLinks) {
    const clean = link.replace(/\+/g, ' ').replace(/\.md$/i, '')
    const name = clean.split('/').filter(Boolean).pop()?.toLowerCase()
    if (name) names.add(name)
  }
  return names
}

export interface CoverageOptions {
  /**
   * Question IDs the user has already answered today. These are held back from
   * the draw: the selection only reaches for them once the unseen questions
   * can't cover a concept (or can't fill the requested count). That's what makes
   * re-launching today's plan — "Continue Studying" after the plan is finished —
   * serve new questions instead of replaying the ones just answered.
   */
  seenIds?: ReadonlySet<string>
}

/**
 * Pick up to `count` questions from `questions` that cover as many of
 * `concepts` as possible, using the fewest questions necessary.
 *
 * Phase 1 greedily picks the question covering the most not-yet-covered
 * concepts at each step, so a quiz with `count >= concepts.length` covers
 * every concept (at least one question each) and a quiz with
 * `count < concepts.length` still spreads across as many distinct concepts
 * as those questions can reach.
 *
 * Phase 2 fills any remaining slots with the highest-coverage leftover
 * questions, so extra questions still stay on-topic.
 *
 * Both phases run over the unseen questions first and only then over
 * `options.seenIds` (see {@link CoverageOptions}), so a concept whose bank
 * still has fresh questions never repeats one — a concept whose bank is
 * exhausted for the day falls back to a repeat rather than being dropped.
 *
 * When `count` is omitted, no cap is applied and phase 2 is skipped: the
 * result is the minimum greedy set that covers every reachable concept —
 * the fewest questions that complete the whole plan.
 */
export function selectQuestionsForCoverage<T extends CoverageQuestion>(
  questions: T[],
  concepts: string[],
  count?: number,
  options?: CoverageOptions,
): T[] {
  const conceptSet = new Set(concepts.map(c => c.toLowerCase()))
  const uncovered = new Set(conceptSet)
  const selected: T[] = []
  const cap = count ?? Infinity

  const seenIds = options?.seenIds
  // Two pools in priority order: never-answered-today first, repeats last.
  const pools = seenIds && seenIds.size > 0
    ? [questions.filter(q => !seenIds.has(q.id)), questions.filter(q => seenIds.has(q.id))]
    : [[...questions]]

  for (const pool of pools) {
    while (uncovered.size > 0 && selected.length < cap && pool.length > 0) {
      let bestIdx = -1
      let bestCovered: Set<string> = new Set()
      for (let i = 0; i < pool.length; i++) {
        const covered = new Set([...linkedConceptNames(pool[i].wiki_link)].filter(c => uncovered.has(c)))
        if (covered.size > bestCovered.size) {
          bestCovered = covered
          bestIdx = i
        }
      }
      if (bestIdx === -1) break
      const [chosen] = pool.splice(bestIdx, 1)
      selected.push(chosen)
      for (const c of bestCovered) uncovered.delete(c)
    }
  }

  if (count !== undefined) {
    for (const pool of pools) {
      if (selected.length >= count) break
      pool.sort((a, b) => {
        const scoreA = [...linkedConceptNames(a.wiki_link)].filter(c => conceptSet.has(c)).length
        const scoreB = [...linkedConceptNames(b.wiki_link)].filter(c => conceptSet.has(c)).length
        return scoreB - scoreA
      })
      for (const q of pool) {
        if (selected.length >= count) break
        selected.push(q)
      }
    }
  }

  return selected
}

/**
 * The fewest questions needed to cover every reachable concept in `concepts`
 * — i.e. the number of questions a "Today's Quiz" needs to give the user a
 * shot at completing the entire day's plan. Concepts with no matching question
 * simply can't be covered and don't inflate the count.
 *
 * Takes the same `options` as {@link selectQuestionsForCoverage} so the count
 * shown on a "questions left today" badge matches the quiz that button starts.
 */
export function minQuestionsToCoverConcepts<T extends CoverageQuestion>(
  questions: T[],
  concepts: string[],
  options?: CoverageOptions,
): number {
  return selectQuestionsForCoverage(questions, concepts, undefined, options).length
}
